
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Menu, 
  X, 
  Home, 
  Calendar, 
  Stethoscope, 
  MessageSquare, 
  Settings,
  ShoppingCart,
  LogOut,
  Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { DashboardHomeSection } from "@/components/professional/dashboard/mobile/DashboardHomeSection";
import { AppointmentsSection } from "@/components/professional/dashboard/mobile/AppointmentsSection";
import { ServicesSection } from "@/components/professional/dashboard/mobile/ServicesSection";
import { PurchaseRequestsSection } from "@/components/professional/dashboard/mobile/PurchaseRequestsSection";
import { MessagesSection } from "@/components/professional/dashboard/mobile/MessagesSection";
import { ProfileSection } from "@/components/professional/dashboard/mobile/ProfileSection";
import { AppointmentForm } from "@/components/appointments/AppointmentForm";
import { ServiceForm } from "@/components/professional/ServiceForm";

interface MenuItem {
  name: string;
  component: React.ComponentType<any>;
  icon: React.FC<any>;
}

const menuItems: MenuItem[] = [
  { name: "Dashboard", component: DashboardHomeSection, icon: Activity },
  { name: "Rendez-vous", component: AppointmentsSection, icon: Calendar },
  { name: "Services et Produits", component: ServicesSection, icon: Stethoscope },
  { name: "Demandes d'achat", component: PurchaseRequestsSection, icon: ShoppingCart },
  { name: "Messages", component: MessagesSection, icon: MessageSquare },
  { name: "Profil Utilisateur", component: ProfileSection, icon: Settings }
];

export default function MobileProfessionalDashboard() {
  const [activeSection, setActiveSection] = useState('Dashboard');
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAppointmentFormOpen, setIsAppointmentFormOpen] = useState(false);
  const [isServiceFormOpen, setIsServiceFormOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Fetch dashboard data
  const { data: stats, isLoading } = useQuery({
    queryKey: ["mobile-professional-dashboard-stats"],
    queryFn: async () => {
      if (!user) return null;
      
      console.log('Fetching dashboard stats for user:', user.id);

      const [appointmentsResult, servicesResult, messagesResult, purchaseRequestsResult, productsResult] = await Promise.all([
        supabase.from("professional_appointments").select("*").eq("professional_id", user.id),
        supabase.from("professional_services").select("*").eq("professional_id", user.id),
        supabase.from("messages").select("*").eq("receiver_id", user.id),
        supabase.from("product_purchase_requests").select("*").eq("professional_id", user.id),
        supabase.from("professional_products").select("*").eq("professional_id", user.id)
      ]);

      const stats = {
        appointments: appointmentsResult.data?.length || 0,
        services: servicesResult.data?.length || 0,
        messages: messagesResult.data?.length || 0,
        purchaseRequests: purchaseRequestsResult.data?.length || 0,
        products: productsResult.data?.length || 0
      };

      console.log('Dashboard data fetched:', stats);
      return stats;
    },
    enabled: !!user,
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const getMenuItemComponent = (item: any) => {
    switch (item.name) {
      case "Dashboard":
        return () => <DashboardHomeSection 
          stats={stats} 
          onNavigate={setActiveSection}
          onNewAppointment={() => setIsAppointmentFormOpen(true)}
          onNewService={() => setIsServiceFormOpen(true)}
        />;
      case "Rendez-vous":
        return () => <AppointmentsSection />;
      case "Services et Produits":
        return () => <ServicesSection />;
      case "Demandes d'achat":
        return () => <PurchaseRequestsSection />;
      case "Messages":
        return () => <MessagesSection />;
      case "Profil Utilisateur":
        return () => <ProfileSection />;
      default:
        return item.component;
    }
  };

  const ActiveComponent = menuItems.find(item => item.name === activeSection)
    ? getMenuItemComponent(menuItems.find(item => item.name === activeSection)!)
    : () => <div className="p-4 text-center text-red-600">Section non trouvée</div>;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Mobile */}
      <header className="bg-blue-600 text-white p-4 flex justify-between items-center sticky top-0 z-50">
        <div>
          <h1 className="text-lg font-bold">Médicace</h1>
          <p className="text-xs opacity-90">{activeSection}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMenuOpen(!menuOpen)}
          className="text-white hover:bg-blue-500"
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </header>

      {/* Menu Mobile Overlay */}
      {menuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50" 
          onClick={() => setMenuOpen(false)}
        >
          <div 
            className="fixed top-0 right-0 h-full w-64 bg-white shadow-lg transform transition-transform overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b">
              <h2 className="font-bold text-gray-900">Menu</h2>
            </div>
            <nav className="p-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={item.name} 
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      activeSection === item.name 
                        ? 'bg-blue-100 text-blue-600 font-semibold' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={() => {
                      setActiveSection(item.name);
                      setMenuOpen(false);
                    }}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm">{item.name}</span>
                    {item.name === "Demandes d'achat" && stats?.purchaseRequests > 0 && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {stats.purchaseRequests}
                      </Badge>
                    )}
                    {item.name === "Messages" && stats?.messages > 0 && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {stats.messages}
                      </Badge>
                    )}
                    {item.name === "Rendez-vous" && stats?.appointments > 0 && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {stats.appointments}
                      </Badge>
                    )}
                  </div>
                );
              })}
              <div className="border-t mt-4 pt-4">
                <div 
                  className="flex items-center gap-3 p-3 rounded-lg cursor-pointer text-red-600 hover:bg-red-50"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4" />
                  <span className="text-sm">Déconnexion</span>
                </div>
              </div>
            </nav>
          </div>
        </div>
      )}

      {/* Contenu Principal */}
      <main className="pb-4">
        <ActiveComponent />
      </main>

      <AppointmentForm 
        open={isAppointmentFormOpen}
        onOpenChange={setIsAppointmentFormOpen}
        onSuccess={() => setIsAppointmentFormOpen(false)}
      />
      <ServiceForm
        open={isServiceFormOpen}
        onOpenChange={setIsServiceFormOpen}
      />
    </div>
  );
}
